import type { Ticket, TicketType } from '../domain/types';
import { calculateStats } from '../domain/stats';
import {
  PairingService,
  SpaceReplacementRequiredError,
  type AcceptInviteInput,
} from '../features/pairing/service';
import { renderPairingView } from '../features/pairing/view';
import { renderShareCard } from '../features/share/card';
import { createBrowserSharePlatform, shareInteraction } from '../features/share/service';
import { renderSettingsView, type SettingsPanel } from '../features/settings/view';
import { renderCreateView } from '../features/tickets/create-view';
import { renderTicketDetail } from '../features/tickets/detail-view';
import { renderHomeView, statusLabels } from '../features/tickets/home-view';
import { renderTicketList } from '../features/tickets/list-view';
import { renderReceiveView } from '../features/tickets/receive-view';
import {
  TicketService,
  type IncomingTicketResult,
} from '../features/tickets/service';
import { fromBase64Url } from '../protocol/base64url';
import type { MessagePayload } from '../protocol/compact';
import { decodeInviteLink, decodeMessageLink, encodeMessageLink } from '../protocol/links';
import { compressImage } from '../storage/images';
import { exportBackup, importBackup, inspectBackup } from '../storage/backup';
import type { AppRepositoryContract, BackupRepositoryContract } from '../storage/repository';
import { parseHashRoute } from './routes';
import { renderShell } from './shell';
import {
  actionNotice,
  backupErrorMessage,
  commandFor,
  errorMessage,
  protocolErrorCode,
  protocolErrorMessage,
} from './controller-support';

const APP_VERSION = '1.0.0';

export class AppController {
  private readonly pairingService: PairingService;
  private readonly ticketService: TicketService;
  private renderId = 0;
  private duplicateDraft: import('../domain/types').TicketDraft | null = null;

  constructor(
    private readonly root: HTMLElement,
    private readonly repo: BackupRepositoryContract,
    private readonly location: Location = window.location,
  ) {
    const baseUrl = `${location.origin}${location.pathname}`;
    this.pairingService = new PairingService(repo, baseUrl);
    this.ticketService = new TicketService(repo, baseUrl);
  }

  start(): void {
    window.addEventListener('hashchange', () => void this.render());
    void this.render();
  }

  async render(): Promise<void> {
    const renderId = ++this.renderId;
    const route = parseHashRoute(this.location.hash);
    const shell = renderShell(this.root, route);
    const page = requirePage(shell);

    if (route.name === 'home') {
      const space = await this.repo.getSpace();
      if (!space) {
        if (renderId === this.renderId) this.renderPairingCreate(page);
        return;
      }
      const tickets = await this.repo.listTickets();
      if (renderId === this.renderId) renderHomeView(page, { space, tickets });
      return;
    }

    if (route.name === 'invite') {
      await this.renderInvite(page, route.token, renderId);
      return;
    }

    if (route.name === 'message') {
      await this.renderMessage(page, route.token, renderId);
      return;
    }

    if (route.name === 'settings') {
      await this.renderSettings(page, renderId);
      return;
    }

    const space = await this.repo.getSpace();
    if (renderId !== this.renderId) return;
    if (!space) {
      this.renderPairingCreate(page);
      return;
    }

    if (route.name === 'create') {
      this.renderTicketCreate(page, route.type, space, this.duplicateDraft?.type === route.type ? this.duplicateDraft : undefined);
      this.duplicateDraft = null;
      return;
    }

    if (route.name === 'tickets') {
      renderTicketList(page, await this.repo.listTickets());
      return;
    }

    if (route.name === 'ticket') {
      const ticket = await this.repo.getTicket(route.ticketId);
      if (!ticket || ticket.coupleId !== space.coupleId) {
        renderPageError(page, '找不到这张单');
        return;
      }
      await this.renderTicketPage(page, space, ticket);
      return;
    }

    renderPageError(page, '找不到这个页面');
  }

  private async renderSettings(page: HTMLElement, renderId: number): Promise<void> {
    let space = await this.repo.getSpace();
    let tickets = space ? await this.repo.listTickets() : [];
    const persistedConflicts = await this.repo.listBackupConflicts();
    if (renderId !== this.renderId) return;
    let pendingImport: { backup: Blob; passphrase: string } | null = null;

    const show = (
      panel: SettingsPanel = { kind: 'idle' },
      options: { notice?: string; error?: string } = {},
    ): void => {
      const stats = space ? calculateStats(tickets, new Date().toISOString(), space.anniversary) : null;
      renderSettingsView(page, { space, stats, panel, ...options }, {
        onOpenExport: () => show({ kind: 'export' }),
        onExport: async (passphrase, confirmation) => {
          if (passphrase !== confirmation || passphrase.trim().length === 0) {
            show({ kind: 'export' }, { error: '两次输入的备份口令必须一致且不能为空。' });
            return;
          }
          try {
            const backup = await exportBackup(this.repo, passphrase);
            downloadBlob(backup, `两小只-备份-${new Date().toISOString().slice(0, 10)}.json`);
            show({ kind: 'idle' }, { notice: '加密备份已生成，请妥善保存文件和口令。' });
          } catch (cause) {
            show({ kind: 'export' }, { error: backupErrorMessage(cause) });
          }
        },
        onInspectImport: async (backup, passphrase) => {
          try {
            const preview = await inspectBackup(backup, passphrase);
            pendingImport = { backup, passphrase };
            show({ kind: 'import-preview', preview });
          } catch (cause) {
            pendingImport = null;
            show({ kind: 'idle' }, { error: backupErrorMessage(cause) });
          }
        },
        onRejectOversizedImport: () => {
          pendingImport = null;
          show({ kind: 'idle' }, { error: '备份文件不能超过 32 MiB，现有数据未更改。' });
        },
        onConfirmImport: async () => {
          if (!pendingImport) {
            show({ kind: 'idle' }, { error: '请重新选择备份并完成预览。' });
            return;
          }
          try {
            const result = await importBackup(this.repo, pendingImport.backup, pendingImport.passphrase);
            pendingImport = null;
            space = await this.repo.getSpace();
            tickets = space ? await this.repo.listTickets() : [];
            const conflicts = await this.repo.listBackupConflicts();
            if (conflicts.length > 0) {
              show({ kind: 'backup-conflicts', conflicts }, {
                notice: `备份已恢复：新增 ${result.applied} 张；${conflicts.length} 个分叉需要明确选择。`,
              });
            } else show({ kind: 'idle' }, {
              notice: `备份已恢复：新增 ${result.applied} 张，重复 ${result.duplicates} 张，冲突 ${result.conflicts} 张。`,
            });
          } catch (cause) {
            pendingImport = null;
            show({ kind: 'idle' }, { error: backupErrorMessage(cause) });
          }
        },
        onRegenerateInvite: async () => {
          try {
            show({ kind: 'invite', inviteUrl: await this.pairingService.regenerateInvite(), fresh: false });
          } catch (cause) {
            show({ kind: 'idle' }, { error: errorMessage(cause) });
          }
        },
        onOpenRebuild: () => show({ kind: 'rebuild' }),
        onRebuild: async (confirmedSpaceName) => {
          const current = space;
          if (!current || confirmedSpaceName !== current.spaceName) return;
          try {
            const result = await this.pairingService.createFreshSpace({
              spaceName: current.spaceName,
              selfName: current.selfName,
              selfAvatar: current.selfAvatar,
              partnerName: current.partnerName,
              anniversary: current.anniversary,
            });
            space = result.space;
            tickets = [];
            pendingImport = null;
            show({ kind: 'invite', inviteUrl: result.inviteUrl, fresh: true });
          } catch (cause) {
            show({ kind: 'rebuild' }, { error: errorMessage(cause) });
          }
        },
        onOpenClear: () => show({ kind: 'clear' }),
        onClear: async (confirmedSpaceName) => {
          if (!space || confirmedSpaceName !== space.spaceName) return;
          try {
            await this.repo.clearAll();
            space = null;
            tickets = [];
            pendingImport = null;
            show({ kind: 'idle' }, { notice: '本机数据已清除' });
          } catch (cause) {
            show({ kind: 'clear' }, { error: errorMessage(cause) });
          }
        },
        onCopy: copyText,
        onResolveBackupConflict: async (id, keep) => {
          if (!space) return;
          try {
            const ticket = await this.repo.resolveBackupConflict(id, keep, space.memberId, new Date().toISOString());
            const shareUrl = await encodeMessageLink(`${this.location.origin}${this.location.pathname}`, { kind: 'ticket', ticket }, fromBase64Url(space.key));
            const remaining = await this.repo.listBackupConflicts();
            show(remaining.length ? { kind: 'backup-conflicts', conflicts: remaining } : { kind: 'idle' }, {
              notice: `已保留${keep === 'local' ? '本机' : '备份'}版本；请复制新链接发送给${space.partnerName}：${shareUrl}`,
            });
          } catch (cause) {
            show({ kind: 'idle' }, { error: errorMessage(cause) });
          }
        },
      });
    };
    show(persistedConflicts.length > 0 ? { kind: 'backup-conflicts', conflicts: persistedConflicts } : { kind: 'idle' });
  }

  private renderPairingCreate(page: HTMLElement, error?: string): void {
    renderPairingView(page, { kind: 'create', error }, {
      onCreate: async (input) => {
        try {
          const result = await this.pairingService.createSpace(input);
          renderPairingView(page, { kind: 'created', ...result }, { onCopy: copyText });
        } catch (cause) {
          this.renderPairingCreate(page, errorMessage(cause));
        }
      },
    });
  }

  private renderTicketCreate(page: HTMLElement, type: TicketType, space: Awaited<ReturnType<AppRepositoryContract['getSpace']>> & {}, initialDraft?: import('../domain/types').TicketDraft): void {
    const showForm = (error?: string, draft?: import('../domain/types').TicketDraft): void => {
      renderCreateView(page, { phase: 'form', type, space, error, draft: draft ?? initialDraft }, {
        onSubmit: async (draft, photo) => {
          let imageId: string | null = null;
          let storableImage: Blob | null = null;
          if (photo) {
            try {
              const image = await compressImage(photo);
              imageId = crypto.randomUUID();
              storableImage = new Blob([await image.arrayBuffer()], { type: image.type });
            } catch {
              showForm('照片处理失败，已移除照片；文字内容已保留，可直接重试', draft);
              return;
            }
          }
          try {
            const result = imageId && storableImage
              ? await this.ticketService.createWithImage(draft, imageId, storableImage, new Date().toISOString())
              : await this.ticketService.create(draft, new Date().toISOString());
            const card = await this.createSharePresentation(result.ticket);
            renderCreateView(page, {
              phase: 'created',
              ...result,
              ...card,
              partnerName: space.partnerName,
            }, { onShare: shareTicket });
          } catch (cause) {
            showForm(errorMessage(cause), draft);
          }
        },
      });
    };
    showForm();
  }

  private async renderTicketPage(
    page: HTMLElement,
    space: NonNullable<Awaited<ReturnType<AppRepositoryContract['getSpace']>>>,
    initialTicket: NonNullable<Awaited<ReturnType<AppRepositoryContract['getTicket']>>>,
  ): Promise<void> {
    const image = initialTicket.imageId ? await this.repo.getImage(initialTicket.imageId) : null;
    const show = (
      ticket = initialTicket,
      options: { shareUrl?: string; shareCard?: Blob; cardError?: string; notice?: string; error?: string } = {},
    ): void => {
      renderTicketDetail(page, { space, ticket, localImage: ticket.id === initialTicket.id ? image : null, ...options }, {
        onShare: shareTicket,
        onAct: async (action, note) => {
          try {
            const command = commandFor(action, space.memberId, note);
            const result = await this.ticketService.act(ticket.id, command, new Date().toISOString());
            const card = await this.createSharePresentation(result.ticket);
            show(result.ticket, {
              shareUrl: result.shareUrl,
              ...card,
              notice: actionNotice(action, result.ticket.type),
            });
          } catch (cause) {
            show(ticket, { error: errorMessage(cause) });
          }
        },
        onDeleteLocal: async () => {
          try {
            await this.repo.deleteTicketWithImage(ticket.id);
            this.location.hash = '#tickets';
          } catch (cause) {
            show(ticket, { error: errorMessage(cause) });
          }
        },
        onCopyAsNew: () => {
          this.duplicateDraft = {
            type: ticket.type, title: ticket.title, description: ticket.description, outcome: ticket.outcome,
            dueDate: ticket.dueDate, importance: ticket.importance, tags: [...ticket.tags], signature: ticket.signature,
            imageId: null,
          };
          this.location.hash = `#create/${ticket.type}`;
        },
      });
    };
    show();
  }

  private async renderInvite(page: HTMLElement, token: string, renderId: number): Promise<void> {
    let invite: ReturnType<typeof decodeInviteLink>;
    try {
      invite = decodeInviteLink(token);
    } catch (cause) {
      this.renderProtocolError(page, protocolErrorCode(cause), protocolErrorMessage(cause));
      return;
    }

    const existing = await this.repo.getSpace();
    if (renderId !== this.renderId) return;
    if (existing?.coupleId === invite.coupleId) {
      renderPairingView(page, { kind: 'existing', space: existing });
      return;
    }

    let pendingIdentity: AcceptInviteInput | undefined;
    const showInvite = (options: { error?: string; replacementWarning?: string } = {}): void => {
      renderPairingView(page, {
        kind: 'invite',
        spaceName: invite.spaceName,
        inviterName: invite.inviterName,
        pendingIdentity,
        ...options,
      }, {
        onAccept: async (identity) => {
          pendingIdentity = identity;
          try {
            const result = await this.pairingService.acceptInvite(token, identity);
            renderPairingView(page, { kind: 'paired', ...result }, { onCopy: copyText });
          } catch (cause) {
            if (cause instanceof SpaceReplacementRequiredError) {
              showInvite({
                replacementWarning: `本机已加入“${cause.currentSpaceName}”；替换后会清除“${cause.currentSpaceName}”的本机记录。`,
              });
            } else {
              showInvite({ error: errorMessage(cause) });
            }
          }
        },
        onReplace: async () => {
          if (!pendingIdentity) return;
          try {
            const result = await this.pairingService.acceptInvite(token, pendingIdentity, { replaceExisting: true });
            renderPairingView(page, { kind: 'paired', ...result }, { onCopy: copyText });
          } catch (cause) {
            showInvite({ error: errorMessage(cause) });
          }
        },
      });
    };
    showInvite();
  }

  private async renderMessage(page: HTMLElement, token: string, renderId: number): Promise<void> {
    renderReceiveView(page, { phase: 'validating' });
    const space = await this.repo.getSpace();
    if (renderId !== this.renderId) return;
    if (!space) {
      this.renderProtocolError(page, 'SPACE_NOT_PAIRED', '请先打开正确的邀请链接完成配对。');
      return;
    }

    let payload: MessagePayload;
    try {
      payload = await decodeMessageLink(token, fromBase64Url(space.key));
    } catch (cause) {
      this.renderProtocolError(page, protocolErrorCode(cause), protocolErrorMessage(cause));
      return;
    }
    if (renderId !== this.renderId) return;

    const incomingCoupleId = payload.kind === 'pairing-receipt' ? payload.coupleId : payload.ticket.coupleId;
    if (incomingCoupleId !== space.coupleId) {
      this.renderProtocolError(page, 'WRONG_SPACE', '这条链接属于另一个双人空间，请打开正确的邀请链接。');
      return;
    }

    if (payload.kind === 'pairing-receipt') {
      this.renderPairingReceipt(page, payload, space);
      return;
    }

    try {
      const preview = await this.ticketService.applyIncoming(token);
      const local = await this.repo.getTicket(preview.ticket.id);
      renderReceiveView(page, {
        phase: 'ticket-preview',
        ticket: preview.ticket,
        isNew: local === null,
        memberId: space.memberId,
        partnerName: space.partnerName,
      }, {
        onCopy: copyText,
        onConfirm: async () => {
          try {
            await this.renderIncomingResult(page, await this.ticketService.confirmIncoming(preview.importId), space.partnerName);
          } catch (cause) {
            this.renderProtocolError(page, 'TICKET_IMPORT_FAILED', errorMessage(cause));
          }
        },
        onAccept: async () => {
          try {
            const imported = await this.ticketService.confirmIncoming(preview.importId);
            if (imported.kind !== 'applied') {
              await this.renderIncomingResult(page, imported, space.partnerName);
              return;
            }
            const result = await this.ticketService.act(
              preview.ticket.id,
              { action: 'accept', actor: space.memberId },
              new Date().toISOString(),
            );
            renderReceiveView(page, {
              phase: 'ticket-result', outcome: 'applied', ticket: result.ticket,
              responseUrl: result.shareUrl, ...await this.createSharePresentation(result.ticket),
              message: '已收下并接受', partnerName: space.partnerName,
            }, { onShare: shareTicket });
          } catch (cause) {
            this.renderProtocolError(page, 'TICKET_ACTION_FAILED', errorMessage(cause));
          }
        },
        onReject: async (note) => {
          try {
            const imported = await this.ticketService.confirmIncoming(preview.importId);
            if (imported.kind !== 'applied') {
              await this.renderIncomingResult(page, imported, space.partnerName);
              return;
            }
            const result = await this.ticketService.act(
              preview.ticket.id,
              { action: 'reject', actor: space.memberId, note },
              new Date().toISOString(),
            );
            renderReceiveView(page, {
              phase: 'ticket-result', outcome: 'applied', ticket: result.ticket,
              responseUrl: result.shareUrl, ...await this.createSharePresentation(result.ticket),
              message: '已收下并拒绝', partnerName: space.partnerName,
            }, { onShare: shareTicket });
          } catch (cause) {
            this.renderProtocolError(page, 'TICKET_ACTION_FAILED', errorMessage(cause));
          }
        },
      });
    } catch (cause) {
      this.renderProtocolError(page, protocolErrorCode(cause), protocolErrorMessage(cause));
    }
  }

  private renderPairingReceipt(
    page: HTMLElement,
    payload: Extract<MessagePayload, { kind: 'pairing-receipt' }>,
    space: NonNullable<Awaited<ReturnType<AppRepositoryContract['getSpace']>>>,
  ): void {
    if (payload.memberId === space.memberId) {
      this.renderProtocolError(page, 'PAIRING_RECEIPT_SELF', '这是本机自己生成的配对回执，不会更改伴侣资料。');
      return;
    }
    if (space.peerReadyAt) {
      this.renderProtocolError(page, 'PAIRING_RECEIPT_REPLAY', '配对回执已经收下；为保护已确认的伴侣资料，本机不会替换资料。');
      return;
    }
    renderReceiveView(page, {
      phase: 'preview',
      title: `${payload.memberName ?? space.partnerName}已完成配对`,
      detail: `收下后将写入伴侣资料：${payload.memberName ?? space.partnerName} ${payload.memberAvatar ?? space.partnerAvatar}。这条回执只能收下一次。`,
      actionLabel: '收下这条更新',
    }, {
      onConfirm: async () => {
        const updatedSpace = await this.repo.recordPartnerReceipt(
          space.coupleId,
          payload.memberName ?? space.partnerName,
          payload.memberAvatar ?? space.partnerAvatar,
          payload.at,
        );
        const responseUrl = await encodeMessageLink(`${this.location.origin}${this.location.pathname}`, {
          kind: 'pairing-receipt',
          coupleId: updatedSpace.coupleId,
          memberId: updatedSpace.memberId,
          memberName: updatedSpace.selfName,
          memberAvatar: updatedSpace.selfAvatar,
          at: new Date().toISOString(),
        }, fromBase64Url(updatedSpace.key));
        renderReceiveView(page, { phase: 'saved', responseUrl }, { onCopy: copyText });
      },
    });
  }

  private async renderIncomingResult(page: HTMLElement, result: IncomingTicketResult, partnerName: string): Promise<void> {
    if (result.kind === 'conflict') {
      renderReceiveView(page, { phase: 'conflict', local: result.local, incoming: result.incoming }, {
        onResolveConflict: async (keep) => {
          try {
            const resolved = await this.ticketService.resolveIncomingConflict(
              result.importId,
              keep,
              new Date().toISOString(),
            );
            renderReceiveView(page, {
              phase: 'ticket-result', outcome: 'applied', ticket: resolved.ticket,
              responseUrl: resolved.shareUrl, ...await this.createSharePresentation(resolved.ticket),
              message: '冲突已解决，请把新链接发给 TA', partnerName,
            }, { onShare: shareTicket });
          } catch (cause) {
            this.renderProtocolError(page, 'TICKET_CONFLICT_RESOLUTION_FAILED', errorMessage(cause));
          }
        },
      });
      return;
    }
    const messages = {
      applied: '已收下这条更新',
      duplicate: '这条更新已经收下',
      stale: '本机已有更新版本',
    } as const;
    const card = await this.createSharePresentation(result.ticket);
    renderReceiveView(page, {
      phase: 'ticket-result',
      outcome: result.kind,
      ticket: result.ticket,
      responseUrl: result.shareUrl,
      ...card,
      message: messages[result.kind],
      partnerName,
    }, { onShare: shareTicket });
  }

  private async createShareCard(ticket: Ticket): Promise<Blob> {
    const imageBlob = ticket.imageId ? await this.repo.getImage(ticket.imageId) : null;
    const image = imageBlob ? await createImageBitmap(imageBlob) : null;
    try {
      return await renderShareCard({
        type: ticket.type,
        title: ticket.title,
        description: ticket.description,
        outcome: ticket.outcome,
        signature: ticket.signature,
        status: statusLabels[ticket.state],
        image,
      });
    } finally {
      image?.close();
    }
  }

  private async createSharePresentation(ticket: Ticket): Promise<{ shareCard?: Blob; cardError?: string }> {
    try {
      return { shareCard: await this.createShareCard(ticket) };
    } catch {
      return { cardError: '分享卡片生成失败，完整链接仍可使用' };
    }
  }

  private renderProtocolError(page: HTMLElement, code: string, message: string): void {
    renderReceiveView(page, {
      phase: 'error',
      errorCode: code,
      appVersion: APP_VERSION,
      message,
    }, { onCopy: copyText });
  }
}

async function copyText(value: string, status: HTMLElement): Promise<void> {
  try {
    await navigator.clipboard.writeText(value);
    status.textContent = '已复制，请粘贴发给 TA。';
  } catch {
    status.textContent = '暂时无法自动复制，请长按上方链接手动复制。';
  }
}

async function shareTicket(url: string, card: Blob | null, status: HTMLElement): Promise<void> {
  const result = await shareInteraction({ url, card }, createBrowserSharePlatform());
  status.textContent = result.message;
}

function requirePage(shell: HTMLElement): HTMLElement {
  const page = shell.querySelector<HTMLElement>('#page');
  if (!page) throw new Error('Missing #page element');
  return page;
}

function renderPageError(page: HTMLElement, message: string): void {
  const error = document.createElement('p');
  error.className = 'error';
  error.setAttribute('role', 'alert');
  error.textContent = message;
  page.append(error);
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.hidden = true;
  document.body.append(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
}
